// Golf Match Play Tracker 2026 -- central data hook

import { useState, useCallback, useEffect } from 'react';
import { db } from '../lib/database';
import { isSupabaseConfigured } from '../lib/supabase';

export function useGolfData() {
  const [golfers, setGolfers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teeSets, setTeeSets] = useState([]);
  const [seasonDates, setSeasonDates] = useState([]);
  const [tiebreaks, setTiebreaks] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const loadData = useCallback(async () => {
    if (!isSupabaseConfigured) {
      setError('Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env, then restart.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const [g, m, c, t, sd, asg, pr, tb, st] = await Promise.all([
        db.golfers.getAll(),
        db.matches.getAll(),
        db.courses.getAll(),
        db.teeSets.getAll(),
        db.seasonDates.getAll(),
        db.assignments.getAll(),
        db.pairings.getAll(),
        db.tiebreaks.getAll(),
        db.settings.getAll(),
      ]);

      // Stitch assignments + division pairings onto their season date so
      // the rest of the app can treat a date as one object.
      const dates = sd.map(d => ({
        ...d,
        assignments: asg
          .filter(a => a.season_date_id === d.id)
          .sort((x, y) => String(x.first_tee_time).localeCompare(String(y.first_tee_time))),
        pairings: pr
          .filter(p => p.season_date_id === d.id)
          .map(p => [p.division_a, p.division_b]),
      }));

      setGolfers(g);
      setMatches(m);
      setCourses(c);
      setTeeSets(t);
      setSeasonDates(dates);
      setTiebreaks(tb);
      setSettings(st);
    } catch (e) {
      console.error('Error loading data:', e);
      setError(e.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const refreshData = useCallback(async () => {
    setRefreshTrigger(p => p + 1);
    await loadData();
  }, [loadData]);

  return {
    golfers, setGolfers,
    matches, setMatches,
    courses, setCourses,
    teeSets, setTeeSets,
    seasonDates, setSeasonDates,
    tiebreaks, setTiebreaks,
    settings, setSettings,
    loading, error, refreshTrigger, refreshData, loadData,
  };
}
